import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import { type JobLine, unitLabel } from '../api/jobs.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ WHS-16 at v1.949: book-out line by line and check-in over as many returns as it takes. The
// database decides what may go and records what follows — the unit's status, a bulk loss, the job's
// status — so these components only say where a line is and send what the person says came back.

export type Condition = 'returned' | 'damaged' | 'missing';

export type CheckInInput =
  | { units: Array<{ assetId: string; condition: Condition }> }
  | { returned: number; damaged: number; missing: number };

/**
 * "2 to go · 1 out · 3 handed on to J-1014 · 6 back · 1 damaged" — only the parts that are not
 * nothing. Gear handed on (INV-A13) never came home, so it is named for where it went, not "back"
 * (the operator's finding at slice 3's checks, 2026-10-01).
 */
export function dispatchLabel(d: JobLine['dispatch']): string {
  const back = [
    ...d.handedOn.map((h) => `${h.quantity} handed on to ${h.reference}`),
    d.returned > 0 ? `${d.returned} back` : null,
    d.damaged > 0 ? `${d.damaged} damaged` : null,
    d.missing > 0 ? `${d.missing} missing` : null,
  ].filter(Boolean);
  return [d.toGo > 0 ? `${d.toGo} to go` : null, d.out > 0 ? `${d.out} out` : null, ...back]
    .filter(Boolean)
    .join(' · ');
}

/** Whether a line has anything ready to book out now: a reserved bulk line, or chosen units. */
export function readyToGo(line: JobLine): boolean {
  if (!line.availability || line.availability.hold !== 'firm') return false;
  return line.serialised
    ? line.allocated.some((u) => u.dispatch === 'allocated')
    : line.dispatch.toGo > 0;
}

/** Units still to go on a serialised line that have no serial chosen yet. */
export function unchosen(line: JobLine): number {
  return line.dispatch.toGo - line.allocated.filter((u) => u.dispatch === 'allocated').length;
}

/**
 * Whether all of what is still to go on a line is ready: a reserved bulk line, or a serialised
 * line with every unit chosen. "Book out all ready lines" counts only these, so "ready" means
 * complete; a line partly chosen goes out by its own Book out (the operator's decision, 2026-09-30).
 */
export function completeToGo(line: JobLine): boolean {
  return readyToGo(line) && (!line.serialised || unchosen(line) === 0);
}

/** What came back on one line: each unit out by condition, or a bulk line's three counts. */
export function CheckInPanel({
  line,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  line: JobLine;
  pending: boolean;
  error: Error | null;
  onSubmit: (input: CheckInInput) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // It opens below the lines; on a phone that is below the fold, so it is brought into view.
  useEffect(() => {
    panel.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);
  return (
    <div ref={panel} className="flex flex-col gap-3 border-2 border-divider p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold">
          Check in {line.description}: {line.dispatch.out} still out
        </p>
        <Button type="button" variant="quiet" onClick={onClose}>
          Done
        </Button>
      </div>
      {line.serialised ? (
        <UnitCheckIn line={line} pending={pending} onSubmit={onSubmit} />
      ) : (
        <CountCheckIn out={line.dispatch.out} pending={pending} onSubmit={onSubmit} />
      )}
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}

function UnitCheckIn({
  line,
  pending,
  onSubmit,
}: {
  line: JobLine;
  pending: boolean;
  onSubmit: (input: CheckInInput) => void;
}) {
  const out = line.allocated.filter((u) => u.dispatch === 'out');
  // Returned unless said otherwise: the common case is one click per return.
  const [conditions, setConditions] = useState<Record<string, Condition>>({});
  const conditionOf = (assetId: string) => conditions[assetId] ?? 'returned';

  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      units: out.map((u) => ({ assetId: u.assetId, condition: conditionOf(u.assetId) })),
    });
  }
  function one(assetId: string) {
    onSubmit({ units: [{ assetId, condition: conditionOf(assetId) }] });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      <ul className="flex flex-col divide-y-2 divide-divider" aria-label="Units out">
        {out.map((u) => (
          <li key={u.assetId} className="flex flex-wrap items-center justify-between gap-3 py-1.5">
            <span className="font-semibold">{unitLabel(u)}</span>
            <span className="flex items-center gap-2">
              <label>
                <span className="sr-only">How {unitLabel(u)} came back</span>
                <select
                  className="input py-1"
                  value={conditionOf(u.assetId)}
                  onChange={(e) =>
                    setConditions({
                      ...conditions,
                      [u.assetId]: e.currentTarget.value as Condition,
                    })
                  }
                >
                  <option value="returned">Returned</option>
                  <option value="damaged">Damaged</option>
                  <option value="missing">Missing</option>
                </select>
              </label>
              <Button
                type="button"
                variant="quiet"
                disabled={pending}
                aria-label={`Check in ${unitLabel(u)}`}
                onClick={() => one(u.assetId)}
              >
                Check in
              </Button>
            </span>
          </li>
        ))}
      </ul>
      {out.length > 1 ? (
        <div>
          <Button type="submit" disabled={pending}>
            Check in all ({out.length})
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function CountCheckIn({
  out,
  pending,
  onSubmit,
}: {
  out: number;
  pending: boolean;
  onSubmit: (input: CheckInInput) => void;
}) {
  const [returned, setReturned] = useState(String(out));
  const [damaged, setDamaged] = useState('0');
  const [missing, setMissing] = useState('0');
  const [problem, setProblem] = useState<string | undefined>();

  function submit(event: FormEvent) {
    event.preventDefault();
    const counts = [returned, damaged, missing].map((v) =>
      /^\d+$/.test(v.trim()) ? Number(v) : NaN,
    );
    if (counts.some(Number.isNaN)) return setProblem('Whole numbers only.');
    const [r, d, m] = counts as [number, number, number];
    if (r + d + m === 0) return setProblem('Say how many came back, and how.');
    if (r + d + m > out) return setProblem(`Only ${out} are still out.`);
    setProblem(undefined);
    onSubmit({ returned: r, damaged: d, missing: m });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-3 sm:max-w-md">
        <Field
          label="Returned"
          inputMode="numeric"
          value={returned}
          onChange={(e) => setReturned(e.currentTarget.value)}
        />
        <Field
          label="Damaged"
          inputMode="numeric"
          value={damaged}
          onChange={(e) => setDamaged(e.currentTarget.value)}
        />
        <Field
          label="Missing"
          inputMode="numeric"
          value={missing}
          onChange={(e) => setMissing(e.currentTarget.value)}
        />
      </div>
      <p className="text-xs text-muted">
        Damaged and missing leave stock. Anything not counted now stays out for the next return.
      </p>
      {problem ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={pending}>
          Check in
        </Button>
      </div>
    </form>
  );
}
